import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  CheckCircle2,
  Coins,
  DatabaseZap,
  Droplets,
  ExternalLink,
  Landmark,
  Lock,
  LogOut,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { EmptyState, Page, PageHeader } from '@/components/shell'
import {
  ContractWalletBar,
  useContractWallet,
  type ContractChainStatus,
  type ExpectedSigner,
} from '@/features/contracts/wallet-bar'
import { PendingAuditBar } from '@/features/contracts/pending-audit-bar'
import { TreasuryOverviewSection } from '@/features/treasury/overview-tab'
import { TaxBreakdownSection } from '@/features/treasury/tax-breakdown-tab'
import {
  api,
  ApiError,
  type TreasuryGraduatedRow,
  type TreasuryRecord,
  type TreasurySection,
  type TreasuryStats,
  type TreasuryStatus,
  type TreasurySyncStatus,
  type TreasurySyncTrigger,
  type TreasuryFullRescan,
  type TreasuryRpcConfig,
  type TreasuryTokenAmount,
  type TreasuryOperationResult,
  type TreasuryConsoleSession,
  type TokenRevenue,
  type TokenRevenueRow,
} from '@/lib/api'
import {
  contractWalletErrorMessage,
  sendTreasuryOperation,
  waitForContractOperation,
  type TreasuryWrite,
} from '@/lib/contract-wallet'
import {
  explorerAddressUrl,
  explorerTxUrl,
  formatDateTime,
  formatUnits,
  shortAddress,
} from '@/lib/format'
import { ThemeToggle } from '@/components/theme-toggle'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/treasury')({
  component: TreasuryConsolePage,
})

function formatUsd(value: number | null): string {
  if (value == null) return '—'
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}

// ── 一次提取/归集的作业 ──────────────────────────────────────────────────
// 单笔与「一键全提」共用同一条流水:每个作业 = 钱包写参数 + 审计上报参数。

type TreasuryJob =
  | { kind: 'claimPlatformFee'; contract: string; quote: string; label: string }
  | { kind: 'lpClaim'; contract: string; locker: 'v4' | 'v2'; currency: string; label: string }
  | {
      kind: 'lpCollect'
      contract: string
      venue: 'v4' | 'v2'
      token: string
      lpTokenId: string | null
      pair: string | null
      label: string
    }

type OperationSpec = {
  title: string
  danger: string
  jobs: TreasuryJob[]
}

/** 服务端 `/treasury/operations` 的入参形状(逐字对齐 treasuryOperationBody)。 */
type TreasuryAuditPayload =
  | { kind: 'claimPlatformFee'; quote: string; reason: string; txHash: string }
  | { kind: 'lpCollect'; token: string; reason: string; txHash: string }
  | { kind: 'lpClaim'; locker: 'v4' | 'v2'; currency: string; reason: string; txHash: string }

const PENDING_STORAGE_KEY = 'contract-treasury-audit-pending'

function readStoredPending(): TreasuryAuditPayload | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(PENDING_STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as TreasuryAuditPayload
    const validKind =
      value.kind === 'claimPlatformFee' || value.kind === 'lpCollect' || value.kind === 'lpClaim'
    if (!validKind || !/^0x[0-9a-fA-F]{64}$/.test(value.txHash)) {
      throw new Error('invalid pending operation')
    }
    return value
  } catch {
    try {
      window.sessionStorage.removeItem(PENDING_STORAGE_KEY)
    } catch {
      // 存储被策略禁用时靠内存锁兜底。
    }
    return null
  }
}

function storePending(pending: TreasuryAuditPayload | null): void {
  try {
    if (pending) window.sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pending))
    else window.sessionStorage.removeItem(PENDING_STORAGE_KEY)
  } catch {
    // 存不下也不能解锁:哈希已经在手上。
  }
}

function jobToWrite(job: TreasuryJob, account: string): TreasuryWrite & { account: string } {
  if (job.kind === 'claimPlatformFee') {
    return { kind: 'claimPlatformFee', contract: job.contract, quote: job.quote, account }
  }
  if (job.kind === 'lpClaim') {
    return { kind: 'lpClaim', contract: job.contract, currency: job.currency, account }
  }
  if (job.venue === 'v4') {
    return { kind: 'lpCollectV4', contract: job.contract, lpTokenId: job.lpTokenId ?? '', account }
  }
  return { kind: 'lpCollectV2', contract: job.contract, pair: job.pair ?? '', account }
}

function jobToAudit(job: TreasuryJob, reason: string, txHash: string): TreasuryAuditPayload {
  if (job.kind === 'claimPlatformFee') {
    return { kind: 'claimPlatformFee', quote: job.quote, reason, txHash }
  }
  if (job.kind === 'lpClaim') {
    return { kind: 'lpClaim', locker: job.locker, currency: job.currency, reason, txHash }
  }
  return { kind: 'lpCollect', token: job.token, reason, txHash }
}

function TreasuryConsoleBody({ username }: { username: string }) {
  const qc = useQueryClient()
  // 独立资金台没有角色概念:登录进来的这个唯一账号即拥有全部操作权。
  const canManage = true
  const [tab, setTab] = useState('overview')

  const status = useQuery({
    queryKey: ['contract-treasury-status'],
    queryFn: () => api.get<TreasuryStatus>('/treasury/status'),
    staleTime: 15_000,
    retry: false,
  })
  const { wallet, walletBusy, connect, switchChain, disconnect } = useContractWallet()

  const [operation, setOperation] = useState<OperationSpec | null>(null)
  const [pending, setPending] = useState<TreasuryAuditPayload | null>(() => readStoredPending())
  const [lastResult, setLastResult] = useState<TreasuryOperationResult | null>(null)
  const [busy, setBusy] = useState(false)

  // 后台同步任务状态:运行中每 4s 轮询,直到 done。
  const syncStatus = useQuery({
    queryKey: ['contract-treasury-sync-status'],
    queryFn: () => api.get<TreasurySyncStatus>('/treasury/sync-status'),
    enabled: canManage,
    refetchInterval: (query) => (query.state.data?.running ? 4_000 : false),
    retry: false,
  })
  // 同步任务完成一轮后刷新资金统计(记录/累计随之更新)。
  useEffect(() => {
    if (syncStatus.data && !syncStatus.data.running) {
      void qc.invalidateQueries({ queryKey: ['contract-treasury-status'] })
    }
  }, [syncStatus.data?.running, syncStatus.data?.lastBlock, qc])

  const data = status.data
  const ready = data?.ready ? data : null
  const onChain = ready !== null && wallet.chainId === ready.chainId
  const canOperate = canManage && onChain && Boolean(wallet.address) && !pending

  const chainStatus: ContractChainStatus | undefined = data
    ? { ready: data.ready, chainId: data.chainId, manager: null, reason: data.reason }
    : undefined

  // 三种 claim 都是无权限、收款人恒=多签,所以任何已连接的操作员钱包都算「可签名」。
  const expected: ExpectedSigner[] = wallet.address
    ? [{ label: '操作员钱包', address: wallet.address }]
    : []

  /** 顺序跑一批作业;任一步交易已发出但未完成审计时置 pending 并中止其余作业。 */
  const runJobs = async (jobs: TreasuryJob[], reason: string) => {
    if (!wallet.address) {
      toast.error('操作失败', { description: '请先连接可签名的钱包。' })
      return
    }
    setBusy(true)
    let done = 0
    try {
      for (const job of jobs) {
        let txHash: string
        try {
          txHash = await sendTreasuryOperation(jobToWrite(job, wallet.address))
        } catch (error) {
          throw new StepError(job.label, contractWalletErrorMessage(error), false, null)
        }
        const audit = jobToAudit(job, reason, txHash)
        setPending(audit)
        storePending(audit)

        try {
          const receipt = await waitForContractOperation(txHash as `0x${string}`)
          if (receipt.status !== 'success') {
            setPending(null)
            storePending(null)
            throw new StepError(job.label, '链上交易已回滚。', false, null)
          }
        } catch (error) {
          if (error instanceof StepError) throw error
          throw new StepError(job.label, contractWalletErrorMessage(error), true, audit)
        }

        try {
          const result = await api.post<TreasuryOperationResult>('/treasury/operations', audit)
          setLastResult(result)
          setPending(null)
          storePending(null)
          done += 1
        } catch (error) {
          const terminal = error instanceof ApiError && error.errorCode === 'transaction_failed'
          if (terminal) {
            setPending(null)
            storePending(null)
            throw new StepError(job.label, '链上交易已回滚，已解除锁定。', false, null)
          }
          throw new StepError(
            job.label,
            error instanceof ApiError ? error.message : '审计接口暂时不可用。',
            true,
            audit,
          )
        }
      }
      setOperation(null)
      toast.success(jobs.length > 1 ? `已完成 ${done} 笔并写入审计` : '操作已确认并写入审计')
    } catch (error) {
      if (error instanceof StepError) {
        setOperation(null)
        if (error.pendingAudit) {
          setPending(error.pendingAudit)
          toast.warning('交易已提交但尚未完成审计', {
            description: `${error.label}：${error.message} 可用补写入口重试，不会再次发送交易。`,
          })
        } else {
          toast.error(done > 0 ? `已完成 ${done} 笔，其余中止` : '操作失败', {
            description: `${error.label}：${error.message}`,
          })
        }
      } else {
        toast.error('操作失败', { description: contractWalletErrorMessage(error) })
      }
    } finally {
      setBusy(false)
      void qc.invalidateQueries({ queryKey: ['contract-treasury-status'] })
    }
  }

  const retryPending = async (overrideHash?: string) => {
    if (!pending) return
    setBusy(true)
    try {
      // overrideHash 允许粘贴真实链上 hash 覆盖存的 safeTxHash。
      const payload = overrideHash ? { ...pending, txHash: overrideHash } : pending
      const result = await api.post<TreasuryOperationResult>('/treasury/operations', payload)
      setLastResult(result)
      setPending(null)
      storePending(null)
      toast.success('补写审计成功')
    } catch (error) {
      const terminal = error instanceof ApiError && error.errorCode === 'transaction_failed'
      if (terminal) {
        setPending(null)
        storePending(null)
        toast.error('链上交易已回滚', { description: '原交易未生效，已解除锁定。' })
      } else {
        toast.warning('补写仍未成功', {
          description: error instanceof ApiError ? error.message : '审计接口暂时不可用。',
        })
      }
    } finally {
      setBusy(false)
      void qc.invalidateQueries({ queryKey: ['contract-treasury-status'] })
    }
  }

  const abandonPending = () => {
    setPending(null)
    storePending(null)
    toast('已放弃该待审计记录', {
      description: '本页已解锁。链上如已成功，此操作将不会留下审计记录。',
    })
  }

  // 一键触发后台同步:立即返回,随后自动轮询 sync-status 直到扫到链头。
  const triggerSync = async () => {
    try {
      const result = await api.post<TreasurySyncTrigger>('/treasury/sync-withdrawals')
      toast.success('alreadyRunning' in result ? '同步已在进行中' : '已开始后台同步', {
        description: '后台正在从链上抓取，进度会自动刷新，无需反复点击。',
      })
      await syncStatus.refetch()
    } catch (error) {
      toast.error('触发同步失败', {
        description: error instanceof ApiError ? error.message : '链上扫描暂时不可用。',
      })
    }
  }

  // 全量重扫:清游标从创世块重扫全部 claim 事件。用于补回增量游标已越过、
  // 当年 RPC 返回不全漏掉的历史提取(幂等去重,不会重复计数;较慢,配 archive RPC 更快)。
  const triggerFullRescan = async () => {
    if (
      !window.confirm(
        '全量重扫会清空扫描游标、从创世块重新扫描全部链上 claim 事件。\n\n用于补回历史上漏计的提取记录(幂等去重,不会重复计数),但耗时较长(建议先配好 archive RPC)。\n\n确定开始?',
      )
    ) {
      return
    }
    try {
      const result = await api.post<TreasuryFullRescan>('/treasury/full-rescan')
      toast.success('alreadyRunning' in result ? '同步已在进行中' : '已开始全量重扫', {
        description: '后台正从创世块重扫,进度会自动刷新,无需反复点击。',
      })
      await syncStatus.refetch()
    } catch (error) {
      toast.error('触发全量重扫失败', {
        description: error instanceof ApiError ? error.message : '链上扫描暂时不可用。',
      })
    }
  }

  return (
    <Page>
      <PageHeader
        title="资金统计"
        description="内盘平台手续费与 LP 平台份额（已 booked）的余额与提取。三种提取都由操作员钱包签名、收款人恒为多签，操作员只付 gas；后台在交易上链后核验并写入审计。LP 部分只显示 locker 已 booked 的费，不预估池子里未 collect 的部分。"
        actions={
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-mut2 sm:inline">
              已登录 <span className="font-medium text-txt">{username}</span>
            </span>
            <ChangePasswordButton />
            <Button
              variant="outline"
              size="sm"
              className="pressable rounded-apple-sm"
              disabled={status.isFetching}
              onClick={() => void status.refetch()}
            >
              <RefreshCw className={cn('size-3.5', status.isFetching && 'animate-spin')} />
              刷新状态
            </Button>
            <LogoutButton />
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" className="h-9 text-[13px]">
            概览
          </TabsTrigger>
          <TabsTrigger value="revenue" className="h-9 text-[13px]">
            资金累计
          </TabsTrigger>
          <TabsTrigger value="tokens" className="h-9 text-[13px]">
            项目币收入
          </TabsTrigger>
          <TabsTrigger value="tax" className="h-9 text-[13px]">
            税收明细
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tax">
          <TaxBreakdownSection />
        </TabsContent>

        <TabsContent value="overview">
          <TreasuryOverviewSection />
        </TabsContent>

        <TabsContent value="revenue" className="space-y-3.5">
          {canManage && (
            <ContractWalletBar
              status={chainStatus}
              isLoading={status.isLoading}
              isError={status.isError}
              error={status.error}
              onRetry={() => void status.refetch()}
              wallet={wallet}
              walletBusy={walletBusy}
              expected={expected}
              onConnect={connect}
              onDisconnect={disconnect}
              onSwitchChain={switchChain}
            />
          )}

          {canManage && (
            <SyncBar
              status={syncStatus.data ?? null}
              canManage={canManage}
              onSync={() => void triggerSync()}
              onFullRescan={() => void triggerFullRescan()}
            />
          )}

          {pending && (
            <PendingAuditBar
              title={
                pending.kind === 'claimPlatformFee'
                  ? '提取内盘手续费'
                  : pending.kind === 'lpClaim'
                    ? '提取 LP 平台份额'
                    : '归集 LP 手续费'
              }
              txHash={pending.txHash}
              busy={busy}
              onRetry={(overrideHash) => void retryPending(overrideHash)}
              onAbandon={abandonPending}
            />
          )}

          {ready && !ready.pricesAvailable && (
            <p className="flex items-center gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] text-warn">
              <TriangleAlert className="size-3.5 shrink-0" />
              价格源暂不可用，余额照常显示，但暂不折算美元。
            </p>
          )}

          {lastResult && !pending && (
            <a
              href={explorerTxUrl(lastResult.txHash)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-apple bg-green/[0.07] px-3.5 py-2.5 text-[11.5px] text-green hover:bg-green/[0.1]"
            >
              <CheckCircle2 className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">
                最近成功：{shortAddress(lastResult.txHash)} · 区块 {lastResult.blockNumber}
              </span>
              <ExternalLink className="size-3 shrink-0" />
            </a>
          )}

          <TreasuryBody
            query={status}
            canOperate={canOperate}
            busy={busy}
            onOperate={setOperation}
          />

          <OperationDialog
            operation={operation}
            busy={busy}
            onOpenChange={(open) => {
              if (!open && !busy) setOperation(null)
            }}
            onSubmit={(reason) => operation && void runJobs(operation.jobs, reason)}
          />
        </TabsContent>

        <TabsContent value="tokens">
          <TokenRevenueSection />
        </TabsContent>
      </Tabs>
    </Page>
  )
}

class StepError extends Error {
  constructor(
    readonly label: string,
    message: string,
    readonly submitted: boolean,
    readonly pendingAudit: TreasuryAuditPayload | null,
  ) {
    super(message)
  }
}

function TreasuryBody({
  query,
  canOperate,
  busy,
  onOperate,
}: {
  query: ReturnType<typeof useQuery<TreasuryStatus>>
  canOperate: boolean
  busy: boolean
  onOperate: (operation: OperationSpec) => void
}) {
  if (query.isLoading) {
    return (
      <div className="space-y-3.5">
        <Skeleton className="h-52 rounded-apple" />
        <Skeleton className="h-72 rounded-apple" />
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <section className="surface p-0">
        <EmptyState
          icon={TriangleAlert}
          title="读不到资金统计"
          description={
            query.error instanceof ApiError ? query.error.message : '请检查后台 API 与网络连接。'
          }
        />
        <div className="flex justify-center pb-6">
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            onClick={() => void query.refetch()}
          >
            <RefreshCw className="size-3.5" />
            重试
          </Button>
        </div>
      </section>
    )
  }

  const status = query.data

  if (!status.ready) {
    return (
      <section className="surface space-y-2 p-6">
        <div className="section-label">链上状态</div>
        <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-3 text-[12px] leading-relaxed text-warn">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">无法读取资金统计</p>
            <p className="mt-1 text-mut">{status.reason ?? '链上校验不可用。'}</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <div className="space-y-3.5">
      {status.stats && <StatsSection stats={status.stats} />}
      <PlatformSection
        section={status.platform}
        canOperate={canOperate}
        busy={busy}
        onOperate={onOperate}
      />
      <LpSection status={status} canOperate={canOperate} busy={busy} onOperate={onOperate} />
    </div>
  )
}

const RECORD_KIND_LABEL: Record<string, string> = {
  fee: '内盘提取',
  lpV4: 'V4 LP 提取',
  lpV2: 'V2 LP 提取',
}

function SyncBar({
  status,
  canManage,
  onSync,
  onFullRescan,
}: {
  status: TreasurySyncStatus | null
  canManage: boolean
  onSync: () => void
  onFullRescan: () => void
}) {
  const running = status?.running ?? false
  const full = status?.full ?? false

  const progress = (() => {
    if (!status)
      return '以链上 claim 事件为准，多签 / EOA / cast 提的都能抓到。点一次即可，后台自动扫到链头。'
    if (status.error) return status.error
    const head = status.headBlock ?? '—'
    const last = status.lastBlock ?? '—'
    const label = full ? '全量重扫' : '同步'
    if (running)
      return `${label}中… 已到块 ${last} / 链头 ${head}，本轮新增 ${status.inserted} 条。`
    if (status.done)
      return `${full ? '全量重扫' : '已同步'}至链头（块 ${head}），本轮新增 ${status.inserted} 条。`
    if (status.startedAt)
      return `上轮${label}到块 ${last} / 链头 ${head}，新增 ${status.inserted} 条。`
    return '以链上 claim 事件为准，多签 / EOA / cast 提的都能抓到。点一次即可，后台自动扫到链头。'
  })()

  return (
    <div className="surface space-y-3 rounded-apple px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <DatabaseZap className={cn('size-4 shrink-0 text-cyan', running && 'animate-pulse')} />
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-medium text-txt">从链上同步提取记录</p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-mut2">{progress}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="pressable rounded-apple-sm text-[12px]"
            disabled={running}
            onClick={onSync}
          >
            {running && !full ? '同步中…' : '从链上同步'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="pressable rounded-apple-sm text-[12px] text-mut2"
            disabled={running}
            onClick={onFullRescan}
            title="清游标从创世块重扫,补回历史漏计的提取(幂等,不重复计数)"
          >
            {running && full ? '重扫中…' : '全量重扫'}
          </Button>
        </div>
      </div>
      {canManage && <RpcConfigRow rpcSource={status?.rpcSource ?? null} />}
    </div>
  )
}

/**
 * 「同步 RPC」配置：粘贴含 key 的高配额 RPC（如 Ankr），加密存库、扫描器优先用它。
 * 保存后**不回显明文**——只显示脱敏 hint（尾部几位）。
 */
function RpcConfigRow({ rpcSource }: { rpcSource: string | null }) {
  const qc = useQueryClient()
  const [url, setUrl] = useState('')
  const config = useQuery({
    queryKey: ['contract-treasury-rpc-config'],
    queryFn: () => api.get<TreasuryRpcConfig>('/treasury/rpc-config'),
    retry: false,
  })

  const mutation = useMutation({
    mutationFn: (next: string | null) =>
      api.post<TreasuryRpcConfig>('/treasury/rpc-config', next ? { url: next } : {}),
    onSuccess: (result, variables) => {
      qc.setQueryData(['contract-treasury-rpc-config'], result)
      setUrl('')
      toast.success(variables ? '同步 RPC 已保存' : '同步 RPC 已清除', {
        description: variables ? '扫描器将优先使用它，一键即可扫完。' : '将回落到公有 RPC。',
      })
    },
    onError: (error) => {
      toast.error('保存失败', {
        description: error instanceof ApiError ? error.message : '请检查 RPC 地址。',
      })
    },
  })

  const configured = config.data?.configured ?? false
  const busy = mutation.isPending

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-line/60 pt-3">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-mut2">
          同步 RPC：
          {configured ? (
            <span className="text-txt">
              已配置{config.data?.hint ? `（${config.data.hint}）` : ''}
              {rpcSource === 'custom' && '，当前生效'}
            </span>
          ) : (
            <span>
              未配置，使用公有 RPC（有限流、较慢）。粘贴带 key 的高配额 RPC 后一键即可扫完。
            </span>
          )}
        </p>
      </div>
      <Input
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        placeholder="https://rpc.ankr.com/xlayer/…（含 key，保存后不回显）"
        spellCheck={false}
        autoComplete="off"
        className="h-8 min-w-0 flex-1 rounded-apple-sm font-mono text-[11.5px]"
      />
      <Button
        size="sm"
        variant="outline"
        className="pressable shrink-0 rounded-apple-sm text-[12px]"
        disabled={busy || url.trim() === ''}
        onClick={() => mutation.mutate(url.trim())}
      >
        保存
      </Button>
      {configured && (
        <Button
          size="sm"
          variant="ghost"
          className="pressable shrink-0 rounded-apple-sm text-[12px] text-rose hover:bg-rose/10 hover:text-rose"
          disabled={busy}
          onClick={() => mutation.mutate(null)}
        >
          清除
        </Button>
      )}
    </div>
  )
}

function StatsSection({ stats }: { stats: TreasuryStats }) {
  const [showAll, setShowAll] = useState(false)
  const [party, setParty] = useState<'all' | TreasuryRecord['party']>('all')

  const records = useMemo(
    () => (party === 'all' ? stats.records : stats.records.filter((r) => r.party === party)),
    [stats.records, party],
  )
  const shown = showAll ? records : records.slice(0, 12)

  return (
    <section className="surface space-y-5 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <Landmark className="size-4 text-cyan" />
          </div>
          <div>
            <h2 className="text-[15px] font-medium tracking-tight text-txt">资金累计</h2>
            <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-mut2">
              平台累计总收入 = 平台已提取 + 平台当前可提取，<b>只算收款人为平台的部分</b>
              （当前多签， 及转多签前的老 owner 0x6EFa…416A）。上面拆成<b>内盘手续费</b>与{' '}
              <b>LP 费</b>两块。 LP 手续费平台/creator 五五分，creator
              自行领取的单列，不计入平台收入。 已提取以链上 claim 事件为准（多签 / EOA / cast
              提的都算）。
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="stat-num text-[26px] leading-none text-txt">
            {formatUsd(stats.totalUsd)}
          </div>
          <div className="mt-1.5 text-[10.5px] text-mut2">
            内盘手续费{' '}
            <span className="stat-num text-mut">{formatUsd(stats.internalTotalUsd)}</span>
            <span className="mx-1.5 text-dim">·</span>
            LP 费 <span className="stat-num text-mut">{formatUsd(stats.lpTotalUsd)}</span>
          </div>
          <div className="mt-0.5 text-[10.5px] text-mut2">
            平台已提取{' '}
            <span className="stat-num text-mut">{formatUsd(stats.withdrawnUsdTotal)}</span>
            <span className="mx-1.5 text-dim">·</span>
            平台可提取{' '}
            <span className="stat-num text-mut">{formatUsd(stats.claimableUsdTotal)}</span>
          </div>
          <div className="mt-0.5 text-[10.5px] text-mut2">
            creator 已提取{' '}
            <span className="stat-num text-mut">{formatUsd(stats.creatorWithdrawnUsdTotal)}</span>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="section-label">提取记录（链上）</div>
          <div className="flex items-center gap-1">
            {(['all', 'platform', 'creator'] as const).map((p) => (
              <Button
                key={p}
                variant={party === p ? 'default' : 'outline'}
                size="sm"
                className="pressable h-7 rounded-apple-sm px-2.5 text-[11px]"
                onClick={() => {
                  setParty(p)
                  setShowAll(false)
                }}
              >
                {p === 'all' ? '全部' : p === 'platform' ? '平台' : 'creator'}
              </Button>
            ))}
          </div>
        </div>
        {records.length === 0 ? (
          <p className="text-[12px] text-mut2">
            {stats.records.length === 0
              ? '暂无链上提取记录。点上方「从链上同步提取记录」抓取。'
              : '当前筛选下没有记录。'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-[12px]">
                <thead>
                  <tr className="border-b border-line/60 text-left text-[10.5px] text-mut2">
                    <th className="pb-2 font-normal">时间</th>
                    <th className="pb-2 font-normal">类型</th>
                    <th className="pb-2 font-normal">归属</th>
                    <th className="pb-2 font-normal">币种</th>
                    <th className="pb-2 text-right font-normal">金额</th>
                    <th className="pb-2 font-normal">交易</th>
                    <th className="pb-2 font-normal">收款人</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((record) => (
                    <RecordRow key={record.id} record={record} />
                  ))}
                </tbody>
              </table>
            </div>
            {records.length > 12 && (
              <Button
                variant="ghost"
                size="sm"
                className="pressable mt-2 h-8 rounded-apple-sm text-[12px] text-cyan hover:bg-cyan/10 hover:text-cyan"
                onClick={() => setShowAll((value) => !value)}
              >
                {showAll ? '收起' : `展开全部（${records.length} 条）`}
              </Button>
            )}
          </>
        )}
      </div>
    </section>
  )
}

function SortableTh({
  children,
  right,
  onClick,
}: {
  children: React.ReactNode
  right?: boolean
  onClick: () => void
}) {
  return (
    <th className={cn('pb-2 font-normal', right && 'text-right')}>
      <button
        type="button"
        onClick={onClick}
        className="cursor-pointer select-none transition-colors hover:text-txt"
      >
        {children}
      </button>
    </th>
  )
}

/** 每个 meme 项目给平台产生的收入。支持搜索 + 排序。 */
type RevSort =
  | 'symbol'
  | 'internalFeeUsd'
  | 'snipeFeeUsd'
  | 'taxFeeUsd'
  | 'lpUsd'
  | 'totalUsd'
  | 'totalVolumeUsd'
  | 'tradeCount'
/** 展示行 = API 行 + 校准后的 LP / 合计(美元)。 */
type RevRow = TokenRevenueRow & { lpUsd: number | null; totalUsd: number | null }

function TokenRevenueSection() {
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<{ col: RevSort; dir: 'asc' | 'desc' }>({
    col: 'totalUsd',
    dir: 'desc',
  })
  const q = useQuery({
    queryKey: ['treasury-token-revenue'],
    queryFn: () => api.get<TokenRevenue>('/treasury/token-revenue'),
    staleTime: 30_000,
    retry: false,
  })
  // 复用「资金累计」的查询(同 key,React Query 自动去重),拿到平台真实 LP 总额。
  const statusQ = useQuery({
    queryKey: ['contract-treasury-status'],
    queryFn: () => api.get<TreasuryStatus>('/treasury/status'),
    staleTime: 15_000,
    retry: false,
  })

  // 校准:把每项目的 LP 估值按占比分摊到「资金累计」的真实 LP 总额,使两页 LP 合计相等。
  // 拿不到真实总额(或估值为 0)时 scale=1,退回原始估值(下方文案会说明)。
  const realLpUsd = statusQ.data?.stats?.lpTotalUsd ?? null
  const estLpTotal = q.data?.totalLpFeeEstUsd ?? 0
  const calibrated = realLpUsd != null && estLpTotal > 0
  const scale = calibrated ? realLpUsd / estLpTotal : 1
  const lpTotalShown = calibrated ? realLpUsd : (q.data?.totalLpFeeEstUsd ?? 0)
  const internalTotal = q.data?.totalInternalFeeUsd ?? 0
  const snipeTotal = q.data?.totalSnipeFeeUsd ?? 0
  const taxFeeTotal = q.data?.totalTaxFeeUsd ?? 0

  const rows = useMemo<RevRow[]>(() => {
    const data = q.data?.rows ?? []
    const term = query.trim().toLowerCase()
    const filtered = term
      ? data.filter(
          (r) =>
            (r.symbol ?? '').toLowerCase().includes(term) ||
            (r.name ?? '').toLowerCase().includes(term) ||
            r.tokenAddress.toLowerCase().includes(term),
        )
      : data
    const withCalc: RevRow[] = filtered.map((r) => {
      const lpUsd = r.lpFeeEstUsd == null ? null : r.lpFeeEstUsd * scale
      const totalUsd =
        r.internalFeeUsd == null && r.snipeFeeUsd == null && lpUsd == null && r.taxFeeUsd == null
          ? null
          : (r.internalFeeUsd ?? 0) + (r.snipeFeeUsd ?? 0) + (lpUsd ?? 0) + (r.taxFeeUsd ?? 0)
      return { ...r, lpUsd, totalUsd }
    })
    const val = (r: RevRow): number | string =>
      sort.col === 'symbol'
        ? (r.symbol ?? r.name ?? r.tokenAddress).toLowerCase()
        : sort.col === 'tradeCount'
          ? r.tradeCount
          : (r[sort.col] ?? -1)
    const dir = sort.dir === 'asc' ? 1 : -1
    return withCalc.sort((a, b) => {
      const av = val(a)
      const bv = val(b)
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av).localeCompare(String(bv)) * dir
      }
      return (av - bv) * dir
    })
  }, [q.data, query, sort, scale])

  const toggleSort = (col: RevSort) =>
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' },
    )
  const arrow = (col: RevSort) => (sort.col === col ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '')

  if (q.isLoading) return <Skeleton className="h-72 rounded-apple" />
  if (q.isError || !q.data) {
    return (
      <section className="surface p-6">
        <p className="text-[12px] text-mut2">
          读取项目币收入失败。
          <button className="ml-1 text-cyan hover:underline" onClick={() => void q.refetch()}>
            重试
          </button>
        </p>
      </section>
    )
  }

  const data = q.data
  const grandTotal = internalTotal + snipeTotal + lpTotalShown + taxFeeTotal

  return (
    <section className="surface space-y-5 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <Coins className="size-4 text-cyan" />
          </div>
          <div>
            <h2 className="text-[15px] font-medium tracking-tight text-txt">项目币收入</h2>
            <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-mut2">
              每个 meme 项目给平台产生的收入。<b>内盘手续费</b>是链上实数（内盘 1%
              全归平台，被毕业门槛封顶，与总成交额不成正比）。<b>防狙击税</b>同样是链上实数（合约与手续费收在同一笔里，这里按买入额 1% 拆开；2026-09-16
              合约升级之前它归创建者，那之前恒为 0）。<b>LP 费</b>
              {calibrated
                ? '：把「资金累计」里平台实收的 LP 总额，按各项目毕业后成交额占比分摊到项目上，所以 LP 合计与「资金累计」相等；单项目是按占比分摊、非逐池实收。'
                : '（估）= 毕业后 DEX 成交额 × 池费率 0.3% × 平台 50%，是按成交额推的上界估值（暂未取到资金累计的实收总额来校准）。'}
              <b>税收协议费</b>只有协议费模板上线之后发射的币才有：金库从税里先切给平台的那份，
              取金库记账的链上事件实数；以项目币记账的只显示数量，不计入合计。
            </p>
            {!data.protocolFeeAvailable && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-warn">
                <TriangleAlert className="size-3.5 shrink-0" />
                税收协议费数据暂时读不到，这一列按 0 显示，合计偏低。稍后刷新。
              </p>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="stat-num text-[26px] leading-none text-txt">{formatUsd(grandTotal)}</div>
          <div className="mt-1.5 text-[10.5px] text-mut2">
            平台收入合计{calibrated ? '' : '（估）'}
          </div>
          <div className="mt-0.5 text-[10.5px] text-mut2">
            内盘费 <span className="stat-num text-mut">{formatUsd(internalTotal)}</span>
            <span className="mx-1.5 text-dim">·</span>
            防狙击 <span className="stat-num text-mut">{formatUsd(snipeTotal)}</span>
            <span className="mx-1.5 text-dim">·</span>
            LP费{calibrated ? '' : '(估)'}{' '}
            <span className="stat-num text-mut">{formatUsd(lpTotalShown)}</span>
            <span className="mx-1.5 text-dim">·</span>
            协议费 <span className="stat-num text-mut">{formatUsd(taxFeeTotal)}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="section-label">共 {data.rows.length} 个项目</div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="搜索项目名 / 符号 / 地址"
          className="h-8 w-full max-w-[240px] text-[12px]"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-[12px]">
          <thead>
            <tr className="border-b border-line/60 text-left text-[10.5px] text-mut2">
              <SortableTh onClick={() => toggleSort('symbol')}>项目{arrow('symbol')}</SortableTh>
              <th className="pb-2 font-normal">状态</th>
              <SortableTh right onClick={() => toggleSort('snipeFeeUsd')}>
                防狙击税{arrow('snipeFeeUsd')}
              </SortableTh>
              <SortableTh right onClick={() => toggleSort('internalFeeUsd')}>
                内盘手续费{arrow('internalFeeUsd')}
              </SortableTh>
              <SortableTh right onClick={() => toggleSort('lpUsd')}>
                {calibrated ? 'LP费·分摊' : 'LP费·估'}
                {arrow('lpUsd')}
              </SortableTh>
              <SortableTh right onClick={() => toggleSort('taxFeeUsd')}>
                税收协议费{arrow('taxFeeUsd')}
              </SortableTh>
              <SortableTh right onClick={() => toggleSort('totalUsd')}>
                {calibrated ? '合计' : '合计·估'}
                {arrow('totalUsd')}
              </SortableTh>
              <SortableTh right onClick={() => toggleSort('totalVolumeUsd')}>
                成交额{arrow('totalVolumeUsd')}
              </SortableTh>
              <SortableTh right onClick={() => toggleSort('tradeCount')}>
                笔数{arrow('tradeCount')}
              </SortableTh>
              <th className="pb-2 font-normal">最近成交</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.tokenAddress} className="border-b border-line/40">
                <td className="py-2">
                  <span className="font-medium text-txt">
                    {r.symbol ?? shortAddress(r.tokenAddress)}
                  </span>
                  {r.name && r.name !== r.symbol && (
                    <span className="ml-1.5 text-[10.5px] text-mut2">{r.name}</span>
                  )}
                  <a
                    href={explorerAddressUrl(r.tokenAddress)}
                    target="_blank"
                    rel="noreferrer"
                    title={r.tokenAddress}
                    className="ml-1.5 font-mono text-[10px] text-mut2 transition-colors hover:text-cyan"
                  >
                    {shortAddress(r.tokenAddress)}
                  </a>
                </td>
                <td className="py-2">
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-px text-[10.5px]',
                      r.graduated ? 'bg-green/12 text-green' : 'bg-edge/10 text-mut2',
                    )}
                  >
                    {r.graduated ? `已毕业${r.venue ? ` · ${r.venue.toUpperCase()}` : ''}` : '内盘'}
                  </span>
                </td>
                <td className="stat-num py-2 text-right text-txt">
                  {formatUnits(r.snipeFeeRaw, r.quoteDecimals, 4)}
                  {r.quoteSymbol && (
                    <span className="ml-1 text-[10px] text-mut2">{r.quoteSymbol}</span>
                  )}
                  {r.snipeFeeUsd != null && (
                    <span className="ml-1.5 text-[10px] text-mut2">
                      {formatUsd(r.snipeFeeUsd)}
                    </span>
                  )}
                </td>
                <td className="stat-num py-2 text-right text-txt">
                  {formatUnits(r.internalFeeRaw, r.quoteDecimals, 4)}
                  {r.quoteSymbol && (
                    <span className="ml-1 text-[10px] text-mut2">{r.quoteSymbol}</span>
                  )}
                  {r.internalFeeUsd != null && (
                    <span className="ml-1.5 text-[10px] text-mut2">
                      {formatUsd(r.internalFeeUsd)}
                    </span>
                  )}
                </td>
                <td className="stat-num py-2 text-right text-mut">{formatUsd(r.lpUsd)}</td>
                <td className="stat-num py-2 text-right text-mut">
                  {r.taxFeeUsd ? formatUsd(r.taxFeeUsd) : r.taxFeeTokenRaw === '0' ? '—' : null}
                  {r.taxFeeTokenRaw !== '0' && (
                    <span className="ml-1.5 text-[10px] text-mut2">
                      +{formatUnits(r.taxFeeTokenRaw, 18, 2)} {r.symbol ?? ''}
                    </span>
                  )}
                </td>
                <td className="stat-num py-2 text-right text-txt">{formatUsd(r.totalUsd)}</td>
                <td className="stat-num py-2 text-right text-mut">{formatUsd(r.totalVolumeUsd)}</td>
                <td className="stat-num py-2 text-right text-mut">{r.tradeCount}</td>
                <td className="py-2 text-mut2">
                  {r.lastTradeAt ? formatDateTime(r.lastTradeAt) : '—'}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="py-4 text-center text-[12px] text-mut2">
                  {data.rows.length === 0
                    ? '暂无有内盘手续费的项目。'
                    : `没有匹配「${query}」的项目。`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-mut2">
        {calibrated ? (
          <>
            <b>LP 费为分摊值</b>：平台实收 LP 总额（{formatUsd(realLpUsd)}
            ，来自「资金累计」）按各项目成交额占比分到项目上， 所以 LP
            合计与「资金累计」一致；单项目非逐池实收。内盘手续费为链上实数。
          </>
        ) : (
          <>
            <b>LP 费为估算</b>（成交额 × 池费率 0.3% × 平台
            50%）；「资金累计」的实收总额暂未取到，无法校准。
          </>
        )}
        {!data.pricesAvailable && ' 价格源暂不可用，美元列可能为空。'}
      </p>
    </section>
  )
}

function RecordRow({ record }: { record: TreasuryRecord }) {
  return (
    <tr className="border-b border-line/40 align-top">
      <td className="py-2 text-mut2">{formatDateTime(record.createdAt)}</td>
      <td className="py-2">
        <span className="rounded-full bg-edge/10 px-1.5 py-px text-[10.5px] text-mut">
          {RECORD_KIND_LABEL[record.kind] ?? record.kind}
        </span>
      </td>
      <td className="py-2">
        <span
          className={cn(
            'rounded-full px-1.5 py-px text-[10.5px]',
            record.party === 'platform' ? 'bg-cyan/12 text-cyan' : 'bg-edge/10 text-mut2',
          )}
        >
          {record.party === 'platform' ? '平台' : 'creator'}
        </span>
      </td>
      <td className="py-2">
        {/* 币种列显示符号（服务端解析 ERC20 symbol），地址作次要信息挂 title。 */}
        <span className="font-medium text-txt">
          {record.symbol ?? shortAddress(record.currency)}
        </span>
        <a
          href={explorerAddressUrl(record.currency)}
          target="_blank"
          rel="noreferrer"
          title={record.currency}
          className="ml-1.5 font-mono text-[10px] text-mut2 transition-colors hover:text-cyan"
        >
          {shortAddress(record.currency)}
        </a>
      </td>
      <td className="stat-num py-2 text-right text-txt">
        {formatUnits(record.amount, record.decimals, 4)}
        {record.amountUsd != null && (
          <span className="ml-1.5 text-[10.5px] text-mut2">{formatUsd(record.amountUsd)}</span>
        )}
      </td>
      <td className="py-2">
        <a
          href={explorerTxUrl(record.txHash)}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-[11px] text-mut2 transition-colors hover:text-cyan"
        >
          {shortAddress(record.txHash)}
        </a>
      </td>
      <td className="py-2">
        {record.recipient ? (
          <a
            href={explorerAddressUrl(record.recipient)}
            target="_blank"
            rel="noreferrer"
            title={record.recipient}
            className="font-mono text-[11px] text-mut2 transition-colors hover:text-cyan"
          >
            {shortAddress(record.recipient)}
          </a>
        ) : (
          <span className="text-mut2">—</span>
        )}
      </td>
    </tr>
  )
}

function PlatformSection({
  section,
  canOperate,
  busy,
  onOperate,
}: {
  section: TreasurySection
  canOperate: boolean
  busy: boolean
  onOperate: (operation: OperationSpec) => void
}) {
  const positive = section.rows
  const batchJobs: TreasuryJob[] = section.address
    ? positive.map((row) => ({
        kind: 'claimPlatformFee',
        contract: section.address!,
        quote: row.token,
        label: `内盘费 ${row.symbol ?? shortAddress(row.token)}`,
      }))
    : []

  return (
    <section className="surface p-6">
      <SectionHead
        icon={Coins}
        title="内盘手续费（平台）"
        subtitle="Manager 内盘联合曲线累计的平台手续费，按计价币分列。提取后进多签。"
        totalUsd={section.totalUsd}
        configured={section.configured}
        reason={section.reason}
        address={section.address}
        canBatch={canOperate && batchJobs.length > 1}
        busy={busy}
        onBatch={() =>
          onOperate({
            title: `一键全提内盘手续费（${batchJobs.length} 笔）`,
            danger:
              '将对每个有余额的计价币逐笔发起 claimPlatformFees，收款人恒为多签。请逐笔在钱包中确认。',
            jobs: batchJobs,
          })
        }
      />

      {section.configured && !section.reason && (
        <AmountTable
          rows={positive}
          emptyText="当前没有可提取的内盘手续费。"
          canOperate={canOperate}
          busy={busy}
          actionLabel="提取"
          onAction={(row) =>
            section.address &&
            onOperate({
              title: `提取内盘手续费 · ${row.symbol ?? shortAddress(row.token)}`,
              danger: 'claimPlatformFees 无权限、收款人恒为多签，操作员只付 gas。',
              jobs: [
                {
                  kind: 'claimPlatformFee',
                  contract: section.address,
                  quote: row.token,
                  label: `内盘费 ${row.symbol ?? shortAddress(row.token)}`,
                },
              ],
            })
          }
        />
      )}
    </section>
  )
}

function LpSection({
  status,
  canOperate,
  busy,
  onOperate,
}: {
  status: TreasuryStatus
  canOperate: boolean
  busy: boolean
  onOperate: (operation: OperationSpec) => void
}) {
  const collectJobs: TreasuryJob[] = status.lp.graduated
    .filter((row) => row.claimable)
    .map((row) => {
      const venue: 'v4' | 'v2' = row.venue.toLowerCase() === 'v4' ? 'v4' : 'v2'
      const contract = venue === 'v4' ? status.lp.v4.address : status.lp.v2.address
      return {
        kind: 'lpCollect' as const,
        contract: contract ?? '',
        venue,
        token: row.tokenAddress,
        lpTokenId: row.lpTokenId,
        pair: row.pair,
        label: `${venue === 'v4' ? 'collect' : 'harvest'} ${row.symbol ?? shortAddress(row.tokenAddress)}`,
      }
    })
    .filter((job) => job.contract !== '')

  return (
    <section className="surface p-6">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Droplets className="size-4 text-cyan" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">LP 手续费（平台 50%）</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            只显示 locker 已 booked 的平台份额。先 collect/harvest 把池子里的费搬进 locker，再提取。
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3.5 lg:grid-cols-2">
        <LockerCard
          title="V4 locker"
          section={status.lp.v4}
          locker="v4"
          canOperate={canOperate}
          busy={busy}
          onOperate={onOperate}
        />
        <LockerCard
          title="V2 locker"
          section={status.lp.v2}
          locker="v2"
          canOperate={canOperate}
          busy={busy}
          onOperate={onOperate}
        />
      </div>

      {/* 毕业币的 collect/harvest 清单 */}
      <div className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="section-label">归集（collect / harvest）</div>
          {canOperate && collectJobs.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              className="pressable h-8 rounded-apple-sm text-[12px]"
              disabled={busy}
              onClick={() =>
                onOperate({
                  title: `全部归集（${collectJobs.length} 笔）`,
                  danger:
                    '将对每个毕业币逐笔 collect（V4）或 harvest（V2），把池子里的费搬进 locker。请逐笔在钱包中确认。',
                  jobs: collectJobs,
                })
              }
            >
              全部归集
            </Button>
          )}
        </div>

        {status.lp.graduated.length === 0 ? (
          <p className="mt-3 text-[12px] text-mut2">当前没有已毕业的代币。</p>
        ) : (
          <ul className="mt-3 divide-y divide-line/50">
            {status.lp.graduated.map((row) => (
              <GraduatedRowItem
                key={row.tokenAddress}
                row={row}
                v4Address={status.lp.v4.address}
                v2Address={status.lp.v2.address}
                canOperate={canOperate}
                busy={busy}
                onOperate={onOperate}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function LockerCard({
  title,
  section,
  locker,
  canOperate,
  busy,
  onOperate,
}: {
  title: string
  section: TreasurySection
  locker: 'v4' | 'v2'
  canOperate: boolean
  busy: boolean
  onOperate: (operation: OperationSpec) => void
}) {
  const batchJobs: TreasuryJob[] = section.address
    ? section.rows
        .filter((row) => !row.isProjectToken)
        .map((row) => ({
          kind: 'lpClaim',
          contract: section.address!,
          locker,
          currency: row.token,
          label: `LP ${row.symbol ?? shortAddress(row.token)}`,
        }))
    : []

  return (
    <div className="surface-inset flex flex-col p-4">
      <SectionHead
        compact
        title={title}
        totalUsd={section.totalUsd}
        configured={section.configured}
        reason={section.reason}
        address={section.address}
        canBatch={canOperate && batchJobs.length > 1}
        busy={busy}
        onBatch={() =>
          onOperate({
            title: `${title} 一键全提（${batchJobs.length} 笔）`,
            danger:
              '将对每个有余额的结算币逐笔 claimPlatform，收款人恒为多签。请逐笔在钱包中确认。',
            jobs: batchJobs,
          })
        }
      />
      {section.configured && !section.reason && (
        <AmountTable
          rows={section.rows}
          emptyText="该 locker 暂无已 booked 的平台份额。"
          canOperate={canOperate}
          busy={busy}
          actionLabel="提取"
          onAction={(row) =>
            !row.isProjectToken &&
            section.address &&
            onOperate({
              title: `提取 LP 平台份额 · ${row.symbol ?? shortAddress(row.token)}`,
              danger:
                'claimPlatform 无权限、收款人恒为多签，操作员只付 gas。项目币份额需在池子对手方结算，此处只提结算币。',
              jobs: [
                {
                  kind: 'lpClaim',
                  contract: section.address,
                  locker,
                  currency: row.token,
                  label: `LP ${row.symbol ?? shortAddress(row.token)}`,
                },
              ],
            })
          }
        />
      )}
    </div>
  )
}

function GraduatedRowItem({
  row,
  v4Address,
  v2Address,
  canOperate,
  busy,
  onOperate,
}: {
  row: TreasuryGraduatedRow
  v4Address: string | null
  v2Address: string | null
  canOperate: boolean
  busy: boolean
  onOperate: (operation: OperationSpec) => void
}) {
  const venue: 'v4' | 'v2' = row.venue.toLowerCase() === 'v4' ? 'v4' : 'v2'
  const contract = venue === 'v4' ? v4Address : v2Address
  const actionLabel = venue === 'v4' ? 'collect' : 'harvest'
  const disabled = !canOperate || busy || !row.claimable || !contract

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium text-txt">
            {row.symbol ?? shortAddress(row.tokenAddress)}
          </span>
          <span className="rounded-full bg-edge/10 px-1.5 py-px font-mono text-[10px] text-mut2">
            {venue.toUpperCase()}
          </span>
          <a
            href={explorerAddressUrl(row.tokenAddress)}
            target="_blank"
            rel="noreferrer"
            title={row.tokenAddress}
            className="font-mono text-[10.5px] text-mut2 transition-colors hover:text-cyan"
          >
            {shortAddress(row.tokenAddress)}
          </a>
        </div>
        {!row.claimable && (
          <p className="mt-0.5 text-[11px] text-mut2">
            {venue === 'v4' ? '缺少 V4 LP 头寸 id，无法归集。' : '缺少 V2 pair 地址，无法归集。'}
          </p>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="pressable h-8 shrink-0 rounded-apple-sm text-[12px]"
        disabled={disabled}
        onClick={() =>
          contract &&
          onOperate({
            title: `${actionLabel} · ${row.symbol ?? shortAddress(row.tokenAddress)}`,
            danger:
              venue === 'v4'
                ? 'collect 把该 V4 头寸池子里的费搬进 locker，之后才能提取平台份额。'
                : 'harvest 把该 V2 pair 的费搬进 locker，之后才能提取平台份额。',
            jobs: [
              {
                kind: 'lpCollect',
                contract,
                venue,
                token: row.tokenAddress,
                lpTokenId: row.lpTokenId,
                pair: row.pair,
                label: `${actionLabel} ${row.symbol ?? shortAddress(row.tokenAddress)}`,
              },
            ],
          })
        }
      >
        {actionLabel}
      </Button>
    </li>
  )
}

function SectionHead({
  icon: Icon,
  title,
  subtitle,
  totalUsd,
  configured,
  reason,
  address,
  canBatch,
  busy,
  onBatch,
  compact,
}: {
  icon?: typeof Coins
  title: string
  subtitle?: string
  totalUsd: number
  configured: boolean
  reason: string | null
  address: string | null
  canBatch: boolean
  busy: boolean
  onBatch: () => void
  compact?: boolean
}) {
  return (
    <div
      className={cn('flex flex-wrap items-start justify-between gap-3', compact && 'items-center')}
    >
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <Icon className="size-4 text-cyan" />
          </div>
        )}
        <div className="min-w-0">
          <h3
            className={cn(
              'font-medium tracking-tight text-txt',
              compact ? 'text-[13.5px]' : 'text-[15px]',
            )}
          >
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-mut2">{subtitle}</p>
          )}
          {address ? (
            <a
              href={explorerAddressUrl(address)}
              target="_blank"
              rel="noreferrer"
              title={address}
              className="mt-1 inline-flex items-center gap-1 font-mono text-[10.5px] text-mut2 transition-colors hover:text-cyan"
            >
              {shortAddress(address)}
              <ExternalLink className="size-2.5 shrink-0 opacity-60" />
            </a>
          ) : (
            <p className="mt-1 text-[10.5px] text-mut2">未配置合约地址</p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {configured && !reason && (
          <div className="text-right">
            <div className="stat-num text-[18px] leading-none text-txt">{formatUsd(totalUsd)}</div>
            <div className="mt-1 text-[10px] text-mut2">合计（结算币）</div>
          </div>
        )}
        {canBatch && (
          <Button
            variant="outline"
            size="sm"
            className="pressable h-8 rounded-apple-sm text-[12px]"
            disabled={busy}
            onClick={onBatch}
          >
            一键全提
          </Button>
        )}
      </div>
    </div>
  )
}

function AmountTable({
  rows,
  emptyText,
  canOperate,
  busy,
  actionLabel,
  onAction,
}: {
  rows: TreasuryTokenAmount[]
  emptyText: string
  canOperate: boolean
  busy: boolean
  actionLabel: string
  onAction: (row: TreasuryTokenAmount) => void
}) {
  if (rows.length === 0) {
    return <p className="mt-4 text-[12px] text-mut2">{emptyText}</p>
  }
  return (
    <ul className="mt-4 divide-y divide-line/50">
      {rows.map((row) => (
        <li key={row.token} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="text-[13px] font-medium text-txt">
              {row.symbol ?? shortAddress(row.token)}
            </span>
            {row.isProjectToken && (
              <span className="rounded-full bg-edge/10 px-1.5 py-px text-[10px] text-mut2">
                项目币
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="stat-num text-[13px] text-txt">
              {formatUnits(row.raw, row.decimals, 6)}
            </div>
            <div className="text-[10.5px] text-mut2">
              {row.isProjectToken ? '不折美元' : formatUsd(row.usd)}
            </div>
          </div>
          {canOperate && !row.isProjectToken ? (
            <Button
              variant="ghost"
              size="sm"
              className="pressable h-8 shrink-0 rounded-apple-sm text-[12px] text-cyan hover:bg-cyan/10 hover:text-cyan"
              disabled={busy}
              onClick={() => onAction(row)}
            >
              {actionLabel}
            </Button>
          ) : (
            <span className="w-14 shrink-0" aria-hidden="true" />
          )}
        </li>
      ))}
    </ul>
  )
}

function OperationDialog({
  operation,
  busy,
  onOpenChange,
  onSubmit,
}: {
  operation: OperationSpec | null
  busy: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()
  const reasonValid = trimmed.length >= 10 && trimmed.length <= 200

  useEffect(() => {
    if (!operation) setReason('')
  }, [operation])

  return (
    <AlertDialog
      open={Boolean(operation)}
      onOpenChange={(open) => {
        if (busy) return
        onOpenChange(open)
      }}
    >
      <AlertDialogContent className="rounded-apple-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[17px] text-txt">{operation?.title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[12.5px] leading-relaxed text-mut">
            确认后浏览器钱包会逐笔弹出交易签名。每笔上链后，后台会重新解码调用参数，对不上就不写审计。
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3.5">
          {operation && operation.jobs.length > 1 && (
            <p className="rounded-apple bg-edge/[0.06] px-3 py-2 text-[11.5px] text-mut2">
              本次共 <span className="font-mono text-txt">{operation.jobs.length}</span>{' '}
              笔，将按顺序逐笔发起；中途任一笔未完成审计会停下，剩余不再发送。
            </p>
          )}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="treasury-reason" className="text-[12.5px]">
                操作理由
              </Label>
              <span className="text-[10.5px] text-mut2">{reason.length}/200</span>
            </div>
            <Textarea
              id="treasury-reason"
              value={reason}
              maxLength={200}
              placeholder="说明触发原因、对账依据或工单编号（至少 10 个字符）"
              className="min-h-20 resize-none rounded-apple text-[12px]"
              aria-invalid={reason.length > 0 && !reasonValid}
              onChange={(event) => setReason(event.target.value)}
            />
            {reason.length > 0 && !reasonValid && (
              <p className="text-[11px] text-rose">操作理由至少需要 10 个字符。</p>
            )}
          </div>
        </div>

        {operation && (
          <p className="flex items-start gap-2 rounded-apple bg-cyan/[0.06] px-3 py-2.5 text-[11.5px] leading-relaxed text-mut">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-cyan" />
            {operation.danger}
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-apple" disabled={busy}>
            返回检查
          </AlertDialogCancel>
          <Button
            className="pressable rounded-apple"
            disabled={!reasonValid || busy}
            onClick={() => onSubmit(trimmed)}
          >
            {busy ? '等待钱包或链上确认…' : '在钱包中确认'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ── 独立资金台的鉴权外壳 ───────────────────────────────────────────────────
//
// 这一页是独立入口(自己的 URL /treasury + 自己的一个用户名/密码),不套后台外壳、
// 不走角色。先问会话:未设置→设置页;未登录→登录页;已登录→资金台正文。
// 真正的边界在服务端(requireTreasuryConsole),这里只决定渲染什么。

function TreasuryConsolePage() {
  const qc = useQueryClient()
  const session = useQuery({
    queryKey: ['treasury-session'],
    queryFn: () => api.get<TreasuryConsoleSession>('/treasury/session'),
    retry: false,
    staleTime: 0,
  })

  if (session.isLoading) {
    return (
      <TreasuryShell>
        <Skeleton className="mx-auto h-64 w-full max-w-md rounded-apple" />
      </TreasuryShell>
    )
  }

  const data = session.data
  if (!data || !data.authenticated) {
    return (
      <TreasuryShell>
        <TreasuryAuthCard
          configured={data?.configured ?? false}
          onAuthed={() => void qc.invalidateQueries({ queryKey: ['treasury-session'] })}
        />
      </TreasuryShell>
    )
  }

  return <TreasuryConsoleBody username={data.username ?? ''} />
}

/** 登录/设置页共用的极简外壳:一个居中卡片 + 右上角主题切换,不含后台侧栏。 */
function TreasuryShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center px-4">
      <div className="absolute top-5 right-5">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2 text-mut2">
          <Landmark className="size-5 text-cyan" />
          <span className="display-title text-lg text-txt">资金台</span>
        </div>
        {children}
      </div>
    </div>
  )
}

/** 未设置→设置(用户名+密码);已设置→登录。 */
function TreasuryAuthCard({ configured, onAuthed }: { configured: boolean; onAuthed: () => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (busy) return
    if (!configured) {
      if (username.trim().length < 3) {
        toast.error('用户名太短', { description: '至少 3 个字符。' })
        return
      }
      if (password.length < 8) {
        toast.error('密码太短', { description: '至少 8 位。' })
        return
      }
      if (password !== confirm) {
        toast.error('两次输入的密码不一致')
        return
      }
    } else if (username.trim().length === 0 || password.length === 0) {
      return
    }
    setBusy(true)
    try {
      const path = configured ? '/treasury/login' : '/treasury/setup'
      await api.post(path, { username: username.trim(), password })
      toast.success(configured ? '登录成功' : '已设置并登录')
      onAuthed()
    } catch (error) {
      toast.error(configured ? '登录失败' : '设置失败', {
        description: error instanceof ApiError ? error.message : '请稍后重试。',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="surface rounded-apple">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="size-4 text-cyan" />
          {configured ? '登录资金台' : '首次设置资金台'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-mut2">
          {configured
            ? '资金台使用独立的用户名和密码,与后台账号无关。'
            : '这是资金台的首次访问,请设置一个独立的用户名和密码(与后台账号无关)。请立即设置并妥善保管——设置后即以它登录。'}
        </p>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="tc-user">用户名</Label>
            <Input
              id="tc-user"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tc-pass">{configured ? '密码' : '密码（至少 8 位）'}</Label>
            <Input
              id="tc-pass"
              type="password"
              autoComplete={configured ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {!configured && (
            <div className="space-y-1.5">
              <Label htmlFor="tc-pass2">确认密码</Label>
              <Input
                id="tc-pass2"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
          )}
          <Button type="submit" className="w-full pressable" disabled={busy}>
            {busy ? '处理中…' : configured ? '登录' : '设置并进入'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function LogoutButton() {
  const qc = useQueryClient()
  return (
    <Button
      variant="outline"
      size="sm"
      className="pressable rounded-apple-sm"
      onClick={async () => {
        await api.post('/treasury/logout')
        void qc.invalidateQueries({ queryKey: ['treasury-session'] })
        toast('已退出资金台')
      }}
    >
      <LogOut className="size-3.5" />
      退出
    </Button>
  )
}

/** 改密:必须验当前密码。 */
function ChangePasswordButton() {
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const reset = () => {
    setCurrent('')
    setNext('')
    setConfirm('')
  }

  const submit = async () => {
    if (busy) return
    if (next.length < 8) {
      toast.error('新密码太短', { description: '至少 8 位。' })
      return
    }
    if (next !== confirm) {
      toast.error('两次输入的新密码不一致')
      return
    }
    setBusy(true)
    try {
      await api.post('/treasury/password', { currentPassword: current, newPassword: next })
      toast.success('密码已更新')
      reset()
      setOpen(false)
    } catch (error) {
      toast.error('修改失败', {
        description: error instanceof ApiError ? error.message : '请稍后重试。',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) reset()
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="pressable rounded-apple-sm">
          <Lock className="size-3.5" />
          改密码
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>修改资金台密码</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="cp-cur">当前密码</Label>
            <Input
              id="cp-cur"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">新密码（至少 8 位）</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new2">确认新密码</Label>
            <Input
              id="cp-new2"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="submit" className="pressable" disabled={busy}>
              {busy ? '处理中…' : '保存'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
